import { z } from 'zod';

import { createCreateSchema, OptionalStringSchema, RequiredIdSchema } from './common';

/**
 * Request bodies for the review → approval transitions (#496).
 *
 * These live apart from `WorkOrderUpdateSchema` on purpose. A transition has side effects
 * — a push to the property manager, a history row, a state precondition — and routing it
 * through the general-purpose PUT would mean any form submit that forwarded the whole
 * work-order snapshot could trip them. Separate endpoints, separate schemas.
 *
 * ── THESE SCHEMAS SHAPE THE PAYLOAD; THEY DO NOT ENFORCE IT ──
 *
 * `validateBody` is FAIL-OPEN (api/validation.ts): when `safeParse` rejects, it logs a
 * warning and returns the RAW body. So a `.min(1)` here is documentation, not a gate —
 * every precondition below is re-checked in the handler, which is where the 400 actually
 * comes from.
 *
 * `createCreateSchema` (i.e. `.passthrough()`) is load-bearing for the same reason it is
 * in work-order-billing.ts: the client injects `historyLat` / `historyLng` into every
 * write, so a schema without passthrough fails to parse on every real request and drops
 * onto the raw-body path — discarding any `.default()` or transform along the way. That
 * failure is invisible to a direct-call API test, which sends a clean body.
 */

/**
 * POST /work-orders/:id/ready-for-approval
 *
 * `customerDescription` is optional because the common case is "the text I already saved
 * is fine, mark it ready". When it is absent AND the row has none, the handler seeds it
 * from `work_completed` — there must always be exactly one customer-facing string, and a
 * blank one would mean the PM approves a description they never saw.
 */
export const WorkOrderReadyForApprovalSchema = createCreateSchema({
	customerDescription: OptionalStringSchema,
	historyComment: OptionalStringSchema,
});

/**
 * POST /work-orders/:id/return-for-review
 *
 * The note is required — a bounce with no reason just costs both people a round trip.
 * Enforced in the handler (see the fail-open note above), which trims first so whitespace
 * cannot satisfy it.
 */
/**
 * SHAPE only — `note` is a string, not necessarily a non-empty one.
 *
 * The handler owns all three rejections — absent, empty, and whitespace-only — and answers
 * `400 NOTE_REQUIRED`, which the client keys off. Requiring it here would reject first with
 * a generic parse error and that coded response would never be reached. Schema states the
 * shape; the handler states the rule.
 */
export const WorkOrderReturnForReviewSchema = createCreateSchema({
	note: z.string().optional(),
	historyComment: OptionalStringSchema,
});

/** The server's hard ceiling on one bulk-approve chunk. The client loops under it. */
export const APPROVE_CHUNK_CAP = 25;

/**
 * POST /work-orders/bulk-approve
 *
 * Chunked client-loop shape, matching every other bulk endpoint in this app: a short,
 * bounded POST that completes inside the request and reports per-id results so the caller
 * can drive the next chunk. No streaming, no waitUntil.
 *
 * The handler TRUNCATES at `APPROVE_CHUNK_CAP` rather than rejecting an over-long list.
 * Rejecting would be the stricter read of the cap, but this endpoint's whole contract is
 * "tell me what you got through" — a 400 on chunk 3 of 5 leaves the client unable to tell
 * a rejected chunk from a failed one, and the caps exist to bound Worker CPU, not to
 * police the client.
 */
/** Same split: an array of ids, with the emptiness rule left to the handler's `NO_IDS`. */
export const WorkOrderBulkApproveSchema = createCreateSchema({
	workOrderIds: z.array(RequiredIdSchema),
	historyComment: OptionalStringSchema,
});

export type WorkOrderReadyForApprovalInput = z.infer<typeof WorkOrderReadyForApprovalSchema>;
export type WorkOrderReturnForReviewInput = z.infer<typeof WorkOrderReturnForReviewSchema>;
export type WorkOrderBulkApproveInput = z.infer<typeof WorkOrderBulkApproveSchema>;
